<template>
  <full-screen-container>
    <div
      v-if="document && $store.state.customers.selectedCustomer"
      :class="{
        'left-panel-open': leftPanelOpen,
        'right-panel-open': rightPanelOpen,
      }"
      class="document-results-panel"
      data-testid="document-results-panel"
    >
      <document-header
        v-if="shouldShowHeader"
        :document="document"
        @back="close"
        @document-deleted="onDocumentDeleted"
        @toggle-history="onToggleHistory"
      ></document-header>
      <div class="document-results-content full-screen-content">
        <document-left-panel
          v-if="shouldShowLeftPanel"
          :document="document"
          @toggle-panel="onPanelsToggle"
        ></document-left-panel>
        <router-view
          :document="document"
          :rotation="rotation"
          :class="{
            'with-right-panel': shouldShowRightPanel && rightPanelOpen,
          }"
          class="document-container"
          @rotate="rotation = (rotation + 90) % 360"
        />
        <document-right-panel
          v-if="shouldShowRightPanel"
          :customer-id="customerId"
          :document="document"
          :config="rightPanelConfig"
          @toggle-panel="onPanelsToggle"
          @close-overlay="onCloseOverlay"
        ></document-right-panel>
      </div>
    </div>
    <div v-else id="preloader">
      <i-spinner size="xl"></i-spinner>
    </div>
  </full-screen-container>
</template>

<script>
import DocumentHeader from '@/V2/documents/DocumentHeader';
import DocumentRightPanel from '@/V2/documents/document-view/DocumentRightPanel';
import DocumentLeftPanel from '@/V2/documents/document-view/DocumentLeftPanel';
import FullScreenContainer from '@/V2/documents/document-api/FullScreenContainer.vue';
import { mapGetters, mapMutations } from 'vuex';
import { iSpinner } from '@inscribe/signal';

export default {
  components: {
    DocumentHeader,
    DocumentLeftPanel,
    DocumentRightPanel,
    FullScreenContainer,
    iSpinner,
  },
  data() {
    return {
      error: false,
      rotation: 0,
      documentLoading: false,
      leftPanelOpen: true,
      rightPanelOpen: true,
      documentDeleted: false,
      historyOpen: false,
      rightPanelConfig: null,
    };
  },
  computed: {
    ...mapGetters({
      getDocumentById: 'getDocumentById',
    }),
    shouldShowHeader() {
      return (
        this.$route.query?.hideDocHeader?.toString() !== 'true' &&
        !this.viewerOnlyMode
      );
    },
    shouldShowLeftPanel() {
      return (
        this.$route.query?.hideDocLeftPanel?.toString() !== 'true' &&
        !this.viewerOnlyMode
      );
    },
    shouldShowRightPanel() {
      return (
        this.$route.query?.hideDocRightPanel?.toString() !== 'true' &&
        !this.viewerOnlyMode
      );
    },
    viewerOnlyMode() {
      return this.$route.query?.viewerOnlyMode?.toString() === 'true';
    },
    document() {
      const docId = this.$route.params.document_id;
      const document = this.getDocumentById(docId);
      if (!document) {
        this.forceDocumentLoad();
      }
      return document;
    },
    customerId() {
      return this.$route.params.customer_id;
    },
  },
  watch: {
    '$route.params.document_id': {
      immediate: true,
      handler(documentId) {
        const { customer_id: customerId } = this.$route.params;
        this.documentLoadWithData(customerId, documentId);
      },
    },
  },
  mounted() {
    if (Object.keys(this.$store.state.config.rulesSchema).length === 0) {
      this.$store.dispatch('getConfigRulesSchema');
    }
    document.addEventListener('keydown', this.onKeyDown);
    this.resetScoreDistribution();
  },
  destroyed() {
    document.removeEventListener('keydown', this.onKeyDown);
  },

  methods: {
    ...mapMutations(['resetScoreDistribution', 'addDocument']),
    onDocumentDeleted() {
      this.documentDeleted = true;
    },
    onToggleHistory(value) {
      this.historyOpen =
        typeof value === 'undefined' ? !this.historyOpen : value;
      if (this.historyOpen) {
        this.rightPanelConfig = { panel: 'history' };
        this.rightPanelOpen = true;
      } else {
        this.rightPanelConfig = null;
      }
    },
    forceDocumentLoad() {
      if (this.documentLoading !== true && !this.documentDeleted) {
        const { customer_id: customerId, document_id: documentId } =
          this.$route.params;
        this.documentLoading = true;
        this.$store
          .dispatch('getDocumentWithSignals', {
            customerId: customerId,
            documentId: documentId,
          })
          .then(async ({ data }) => {
            this.addDocument(data);
            await this.documentLoadWithData(customerId, documentId);
            this.documentLoading = false;
          })
          .catch(() => {
            this.close();
            this.$notify({
              group: 'general',
              type: 'error',
              title: 'Problem fetching document',
              duration: 5000,
              text: "We couldn't find that document. You might not have permission to view it, or it might have been deleted.",
            });
          });
      }
    },
    documentLoadWithData(customerId, documentId) {
      this.$store
        .dispatch('getDocumentWithData', {
          customerId: customerId,
          documentId: documentId,
        })
        .then(({ data }) => {
          this.$store.commit('updateGeneric', {
            objectsName: 'documents',
            updatedObject: {
              ...this.document,
              ...data,
            },
          });
        });
    },
    onKeyDown(evt) {
      if (evt.key === 'Escape' && !this.viewerOnlyMode) {
        this.close();
      }
    },
    close(forceCustomer) {
      const { return_to, page } = this.$route.query;
      if (return_to && forceCustomer !== true) {
        this.$router.push(decodeURIComponent(return_to));
      } else {
        this.$router.push({
          name: 'customer-overview',
          params: {
            customer_id: this.$route.params.customer_id,
          },
          query: { page },
        });
      }
    },
    onCloseOverlay(type) {
      if (type === 'history') {
        this.historyOpen = false;
      }
    },
    onPanelsToggle(side) {
      if (side === 'left') {
        this.leftPanelOpen = !this.leftPanelOpen;
      } else if (side === 'right') {
        this.rightPanelOpen = !this.rightPanelOpen;

        // move intercom button if necessary
        const intercomLauncher =
          document.getElementsByClassName('intercom-launcher');
        if (intercomLauncher.length) {
          intercomLauncher[0].style.bottom = this.rightPanelOpen
            ? '20px'
            : '70px';
        }
      }
    },
  },
};
</script>

<style lang="scss">
.document-results-panel {
  $right-panel-width: 460px;
  $left-panel-width: 260px;
  $transition-time: 150ms;

  z-index: 1001;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: $light-neutral-30;

  &.left-panel-open {
    .left-panel {
      min-width: $left-panel-width;
      width: $left-panel-width;

      .scroll-container {
        opacity: 1;
      }
    }
  }

  &.right-panel-open {
    .right-panel {
      min-width: $right-panel-width;
      width: $right-panel-width;
    }
  }

  .document-container.with-right-panel {
    padding-right: $right-panel-width !important;
    .pdf-controls {
      right: 480px; // 460 right panel + padding
    }
  }

  .pdf-controls {
    top: 80px;
  }

  .left-panel {
    min-width: 0;
    max-width: 0;

    .scroll-container {
      opacity: 0;
      transition: opacity 150ms ease-in-out;
    }
    transition: width, min-width $transition-time ease-in-out;
  }

  .right-panel {
    position: fixed;
    top: 65px;
    right: 0;
    bottom: 0;
    z-index: 100;
    background: $light-neutral-default;
    min-width: 0;
    max-width: 0;
    border-left: 1px solid $light-neutral-60;
    transition: width, min-width $transition-time ease-in-out;
  }

  .section-title {
    color: $light-neutral-120;
    text-transform: capitalize;
    font-size: $default-font-size;
    font-weight: map-get($text-weights, 'semibold');
  }

  .modal-body {
    background: $color-almost-black-20 !important;
    display: flex;
    max-height: 100%;
    overflow: hidden;
    padding: 0 !important;
  }

  .i-accordion {
    .i-accordion-item {
      border-bottom: 1px solid $light-neutral-60;
      margin-bottom: 0;
      border-radius: 0 !important;

      &.half-margin {
        .i-accordion-item__article {
          margin-left: 12px !important;
          margin-right: 12px !important;
        }
      }

      .container {
        margin-left: 10px !important;
      }

      .i-accordion-item__article {
        margin-bottom: 0 !important;
      }

      .accordion-content {
        padding-bottom: 20px;
        padding-top: 0;
      }
    }
    .i-accordion-item__title {
      .title-container {
        margin-bottom: 16px !important;
      }
      span {
        color: $light-neutral-120;
        font-size: 14px !important;
        font-weight: 600 !important;
      }
    }
  }
}
</style>
